import React from "react";
import { Card } from "react-bootstrap";
import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormInput } from "go-form";
import { FormSelectGroup } from "go-form/components/FormSelect";
import {
	PrintoutTemplateApi,
	PrintoutTemplateFontSizeType,
} from "../../../../../../../../../services/Api/Organization/types";
import {
	getPrintoutTemplateFontOptions,
	getPrintoutTemplateSettingsForDeliveryFont,
	getPrintoutTemplateSettingsForLargeFont,
	getPrintoutTemplateSettingsForSmallFont,
} from "../utils";

interface Props {
	form: UseFormReturn<PrintoutTemplateApi>;
}

const PrintoutTemplateBasicInfoForm = ({ form }: Props) => {
	const { t } = useTranslation();
	const {
		formState: { errors },
		register,
		control,
		setValue,
	} = form;

	const sortOptions = [
		{
			label: t("enums.printout.sort.ALPHABETICAL"),
			value: "ALPHABETICAL",
		},
		{
			label: t("enums.printout.sort.CHRONOLOGICAL"),
			value: "CHRONOLOGICAL",
		},
	];

	const mergeItemsOptions = [
		{
			label: t("enums.printout.merge_items.ORIGINAL"),
			value: "ORIGINAL",
		},
		{
			label: t("enums.printout.merge_items.MERGE"),
			value: "MERGE",
		},
		{
			label: t("enums.printout.merge_items.SPLIT"),
			value: "SPLIT",
		},
	];

	const printingTypeOptions = [
		{
			label: t("enums.printout.printing_type.ASYNC_AFTER_ORDER_CLOSE"),
			value: "ASYNC_AFTER_ORDER_CLOSE",
		},
		{
			label: t("enums.printout.printing_type.SYNC_BEFORE_ORDER_CLOSE"),
			value: "SYNC_BEFORE_ORDER_CLOSE",
		},
	];

	const getParsedOptionsDependsOnFont = (option: PrintoutTemplateFontSizeType) => {
		switch (option) {
			case PrintoutTemplateFontSizeType.LARGE: {
				return Object.entries(getPrintoutTemplateSettingsForLargeFont()).forEach(([key, value]) => {
					setValue(key as keyof PrintoutTemplateApi, value);
				});
			}
			case PrintoutTemplateFontSizeType.DELIVERY: {
				return Object.entries(getPrintoutTemplateSettingsForDeliveryFont()).forEach(([key, value]) => {
					setValue(key as keyof PrintoutTemplateApi, value);
				});
			}
			case PrintoutTemplateFontSizeType.SMALL: {
				return Object.entries(getPrintoutTemplateSettingsForSmallFont()).forEach(([key, value]) => {
					setValue(key as keyof PrintoutTemplateApi, value);
				});
			}
			default: {
				return {};
			}
		}
	};

	const handleChangeFont = async (option: PrintoutTemplateFontSizeType) => {
		return getParsedOptionsDependsOnFont(option);
	};

	return (
		<Card className={"mb-3"}>
			<Card.Body>
				<h6>{t("modules.printout_template.field.legend_info.title")}</h6>
				<div className={"row"}>
					<div className={"col-md-12"}>
						<FormInput
							label={t("modules.printout_template.field.name.title")}
							errors={errors}
							name={"name"}
							register={register}
						/>
					</div>
					<div className={"col-md-12"}>
						<FormSelectGroup
							label={t("modules.printout_template.field.merge_items.title")}
							name="merge_items"
							errors={errors}
							options={mergeItemsOptions}
							control={control}
							data-testid="merge_items"
						/>
					</div>
					<div className={"col-md-6"}>
						<FormSelectGroup
							label={t("modules.printout_template.field.text_size.title")}
							name="text_size"
							errors={errors}
							options={getPrintoutTemplateFontOptions(t, [
								PrintoutTemplateFontSizeType.LARGE_DOUBLE_WIDTH,
								PrintoutTemplateFontSizeType.DOUBLE_WIDTH,
							])}
							control={control}
							data-testid="text_size"
							onChange={(option) => handleChangeFont(option)}
							useConfirmationOnChange
						/>
					</div>
					<div className={"col-md-6"}>
						<FormSelectGroup
							label={t("modules.printout_template.field.items_sort.title")}
							name="items_sort"
							errors={errors}
							options={sortOptions}
							control={control}
							data-testid="items_sort"
						/>
					</div>
					<div className={"col-md-6"}>
						<FormSelectGroup
							label={t("modules.printout_template.field.kitchen_order_printing_type.title")}
							name="kitchen_order_printing_type"
							errors={errors}
							options={printingTypeOptions}
							control={control}
							data-testid="kitchen_order_printing_type"
						/>
					</div>
				</div>
			</Card.Body>
		</Card>
	);
};

export default PrintoutTemplateBasicInfoForm;
